import { useEffect, useState, lazy, Suspense, useCallback } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Drawer } from '@arco-design/web-react'
import { IconArrowRight, IconBook, IconDashboard } from '@arco-design/web-react/icon'
import { Braces, Menu as MenuIcon, Shield, Zap } from 'lucide-react'
import { getUserDisplayName, getUserInitial } from '@/lib/userDisplayName'
import { useAuthStore } from '@/stores/authStore'
import type { AuthPanelView } from '@/components/Auth/AuthPanel'
import LanguageSwitcher from '@/components/Common/LanguageSwitcher'
import QuickStartCard from './QuickStartCard'
import './style.css'

const AuthPanel = lazy(() => import('@/components/Auth/AuthPanel'))

export default function Landing() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)
  const accountLabel = getUserDisplayName(user, t('common.console'))
  const [scrolled, setScrolled] = useState(false)
  const [isAuthMode, setIsAuthMode] = useState(false)
  const [authInitialView, setAuthInitialView] = useState<AuthPanelView>('login')
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const authFromQuery = searchParams.get('auth')

  const openAuth = useCallback(
    (view: AuthPanelView = 'login') => {
      setAuthInitialView(view)
      setIsAuthMode(true)
      const next = new URLSearchParams(searchParams)
      if (view === 'login' || view === 'register') {
        next.set('auth', view)
      }
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const closeAuth = useCallback(() => {
    setIsAuthMode(false)
    const next = new URLSearchParams(searchParams)
    next.delete('auth')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const handleAuthViewChange = useCallback(
    (view: AuthPanelView) => {
      if (view === 'login' || view === 'register') {
        setAuthInitialView(view)
        const next = new URLSearchParams(searchParams)
        next.set('auth', view)
        setSearchParams(next, { replace: true })
      }
    },
    [searchParams, setSearchParams],
  )

  const handleStartBuilding = () => {
    if (isAuthenticated) {
      navigate('/console')
      return
    }
    openAuth('login')
  }

  useEffect(() => {
    if (isAuthenticated) {
      setIsAuthMode(false)
      return
    }
    if (authFromQuery === 'login' || authFromQuery === 'register') {
      setAuthInitialView(authFromQuery)
      setIsAuthMode(true)
    }
  }, [authFromQuery, isAuthenticated])

  useEffect(() => {
    const state = location.state as { openAuth?: boolean; authView?: AuthPanelView } | null
    if (state?.openAuth && !isAuthenticated) {
      openAuth(state.authView ?? 'login')
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.pathname, location.state, isAuthenticated, navigate])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navActions = (
    <>
      <button
        type="button"
        className="landing-nav-icon-btn hidden sm:inline-flex"
        onClick={() => navigate('/docs')}
        aria-label={t('nav.docs')}
      >
        <IconBook />
      </button>
      {isAuthenticated ? (
        <button
          type="button"
          className="landing-nav-account-chip hidden sm:inline-flex"
          onClick={() => navigate('/console')}
          aria-label={t('common.enterConsole')}
        >
          <span className="landing-nav-account-avatar" aria-hidden>
            {getUserInitial(user)}
          </span>
          <span className="landing-nav-account-name">{accountLabel}</span>
          <IconArrowRight className="landing-nav-account-arrow" />
        </button>
      ) : (
        <>
          <button
            type="button"
            className="landing-btn-sm-ghost hidden sm:inline-flex"
            onClick={() => openAuth('login')}
          >
            {t('auth.login')}
          </button>
          <button
            type="button"
            className="landing-btn-sm-soft-primary hidden sm:inline-flex"
            onClick={() => openAuth('register')}
          >
            {t('auth.registerButton')}
          </button>
        </>
      )}
    </>
  )

  return (
    <div className="landing-page landing-page-minimal">
      <header className={`landing-navbar landing-navbar-hero ${scrolled ? 'scrolled' : ''}`}>
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 h-full flex items-center justify-between">
          <button
            type="button"
            className="flex items-center gap-3 border-0 bg-transparent p-0 cursor-pointer"
            onClick={() => navigate('/')}
            aria-label={t('common.backHome')}
          >
            <img src="/logo.svg" alt="TokenTCP" className="w-8 h-8" />
            <span className="text-[20px] font-extrabold tracking-[-0.02em] text-[#0F172A]">
              <span>Token</span>
              <span className="landing-logo-accent">TCP</span>
            </span>
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={t('common.openNavMenu')}
              className="landing-mobile-menu-btn sm:hidden"
              onClick={() => setMobileNavOpen(true)}
            >
              <MenuIcon size={18} />
            </button>
            <LanguageSwitcher variant="landing" />
            <div className="hidden sm:flex items-center gap-2">{navActions}</div>
          </div>
        </div>
      </header>

      <Drawer
        visible={mobileNavOpen}
        placement="right"
        width={280}
        title={t('common.navigation')}
        footer={null}
        onCancel={() => setMobileNavOpen(false)}
        className="landing-mobile-nav-drawer sm:hidden"
      >
        <nav className="flex flex-col gap-2">
          <button
            type="button"
            className="w-full text-left px-4 py-3 rounded-lg text-[15px] font-medium text-[#374151] hover:bg-[#EEF2FF] hover:text-[#4F46E5] transition-colors"
            onClick={() => {
              setMobileNavOpen(false)
              navigate('/docs')
            }}
          >
            {t('nav.docs')}
          </button>
          {isAuthenticated ? (
            <>
              <div className="landing-mobile-account px-4 py-3 rounded-lg bg-[#EEF2FF]/60 border border-[#C7D2FE]/50">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="landing-nav-account-avatar">{getUserInitial(user)}</span>
                  <span className="landing-nav-account-name text-[15px] font-medium text-[#0F172A]">
                    {accountLabel}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="landing-btn-sm-primary w-full justify-center"
                onClick={() => {
                  setMobileNavOpen(false)
                  navigate('/console')
                }}
              >
                <IconDashboard />
                {t('common.enterConsole')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="landing-btn-sm-ghost w-full justify-center"
                onClick={() => {
                  setMobileNavOpen(false)
                  openAuth('login')
                }}
              >
                {t('auth.login')}
              </button>
              <button
                type="button"
                className="landing-btn-sm-soft-primary w-full justify-center"
                onClick={() => {
                  setMobileNavOpen(false)
                  openAuth('register')
                }}
              >
                {t('auth.registerButton')}
              </button>
            </>
          )}
        </nav>
      </Drawer>

      <main>
        <section id="hero" className="landing-hero landing-hero-minimal">
          <div className="landing-hero-bg" aria-hidden>
            <div className="landing-hero-grid" />
            <div className="landing-hero-mesh landing-hero-mesh-indigo" />
            <div className="landing-hero-mesh landing-hero-mesh-cyan" />
            <div className="landing-hero-mesh landing-hero-mesh-violet" />
          </div>

          <div className="relative z-10 max-w-[1200px] mx-auto px-6 w-full">
            <div className="landing-hero-shell">
              <div
                className={`landing-hero-copy ${
                  isAuthMode && !isAuthenticated ? 'landing-hero-copy-dimmed' : ''
                }`}
              >
                <div className="landing-badge mb-8 landing-reveal-up landing-reveal-delay-1">
                  {t('landing.heroBadge')}
                </div>

                <h1 className="landing-hero-title font-extrabold mb-6 landing-reveal-up landing-reveal-delay-2">
                  {t('landing.heroTitlePrefix')}
                  <span className="landing-hero-title-accent">{t('landing.heroTitleAccent')}</span>
                  {t('landing.heroTitleSuffix')}
                </h1>

                <p className="landing-hero-description landing-reveal-up landing-reveal-delay-3">
                  {t('landing.heroDescription')}
                </p>

                <div className="landing-hero-actions landing-reveal-up landing-reveal-delay-4">
                  <button type="button" className="landing-btn-primary" onClick={handleStartBuilding}>
                    {isAuthenticated ? t('common.enterConsole') : t('landing.getStarted')}
                    <IconArrowRight />
                  </button>
                  <button type="button" className="landing-btn-ghost" onClick={() => navigate('/docs')}>
                    <IconBook />
                    {t('landing.viewDocs')}
                  </button>
                </div>

                <div className="landing-hero-trust landing-reveal-up landing-reveal-delay-5">
                  <div className="landing-trust-pill">
                    <span className="landing-trust-pill-icon landing-trust-pill-icon-indigo">
                      <Braces size={14} />
                    </span>
                    <span>{t('landing.heroTrustOpenAI')}</span>
                  </div>
                  <div className="landing-trust-pill">
                    <span className="landing-trust-pill-icon landing-trust-pill-icon-indigo">
                      <Shield size={14} />
                    </span>
                    <span>{t('landing.heroTrustKey')}</span>
                  </div>
                  <div className="landing-trust-pill">
                    <span className="landing-trust-pill-icon landing-trust-pill-icon-violet">
                      <Zap size={14} />
                    </span>
                    <span>{t('landing.heroTrustStream')}</span>
                  </div>
                </div>
              </div>

              <div className={`landing-hero-panel-wrap${isAuthMode && !isAuthenticated ? ' landing-hero-panel-wrap-auth' : ''}`}>
                {isAuthMode && !isAuthenticated ? (
                  <>
                    <button
                      type="button"
                      className="fixed inset-0 z-[35] bg-slate-900/[0.08] backdrop-blur-[2px] lg:hidden"
                      onClick={closeAuth}
                      aria-label={t('auth.closeLogin')}
                    />
                    <div className="landing-hero-panel landing-hero-panel-auth landing-auth-slide-in fixed bottom-0 left-0 right-0 z-40 max-h-[min(88vh,640px)] overflow-y-auto rounded-t-[1.35rem] border-t border-white/50 shadow-[0_-12px_48px_rgba(15,23,42,0.07)] px-4 pt-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] lg:static lg:z-auto lg:max-h-none lg:overflow-visible lg:rounded-[28px] lg:border lg:border-white/68 lg:shadow-none lg:px-0 lg:pt-0 lg:pb-0">
                      <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-gray-200 lg:hidden" />
                      <Suspense
                        fallback={
                          <div className="landing-auth-panel-skeleton mx-auto max-w-[440px] min-h-[300px] rounded-2xl" />
                        }
                      >
                        <AuthPanel
                          embedded
                          onDismiss={closeAuth}
                          initialView={authInitialView}
                          onViewChange={handleAuthViewChange}
                        />
                      </Suspense>
                    </div>
                  </>
                ) : (
                  <QuickStartCard />
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="landing-footer landing-footer-compact">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="landing-footer-row">
            <span className="landing-footer-copy">© 2026 TokenTCP. All rights reserved.</span>
            <nav className="landing-footer-nav">
              <button type="button" className="landing-footer-link" onClick={() => navigate('/docs')}>
                {t('landing.footer.docs')}
              </button>
              <button type="button" className="landing-footer-link" onClick={() => navigate('/about')}>
                {t('landing.footer.about')}
              </button>
              <button type="button" className="landing-footer-link" onClick={() => navigate('/agreement')}>
                {t('landing.footer.agreement')}
              </button>
              <button type="button" className="landing-footer-link" onClick={() => navigate('/policy')}>
                {t('landing.footer.policy')}
              </button>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  )
}
